// <pos-reservations/> — 店家後台訂位清單（W9 提早 MVP）
// 路徑：#/reservations
// 顯示某日訂位清單，可改狀態 (confirmed → seated / cancelled / no_show)
// see netlify/functions/reservation-list.js + reservation-update-status.js

import { listReservations, updateReservationStatus } from '../lib/api.js';
import { getSupabase } from '../lib/supabase-client.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function pad2(n) { return String(n).padStart(2, '0'); }

function todayInTaipei() {
  const tp = new Date(Date.now() + 8 * 60 * 60 * 1000);
  return `${tp.getUTCFullYear()}-${pad2(tp.getUTCMonth() + 1)}-${pad2(tp.getUTCDate())}`;
}

function formatTime(iso) {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const STATUS_LABELS = {
  pending: '待確認',
  confirmed: '已確認',
  waitlist: '候補',
  seated: '已就座',
  completed: '完成',
  cancelled: '已取消',
  no_show: '未到',
};

// 中間調色值：深淺色模式都要可讀（頁面跟隨 app 深色主題）
const STATUS_COLORS = {
  pending: '#f4a261',
  confirmed: '#2a9d8f',
  waitlist: '#999',
  seated: '#4A90B8',
  completed: '#888',
  cancelled: '#D9534F',
  no_show: '#B45252',
};

class PosReservations extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      date: todayInTaipei(),
      reservations: [],
      busyId: null,
    };
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async load() {
    this.state.loading = true;
    this.state.error = null;
    this.render();
    try {
      const data = await listReservations({ date: this.state.date });
      this.state.reservations = data.reservations || [];
      if (!this.state.slug) {
        const { data: t } = await getSupabase().from('tenants').select('slug').maybeSingle();
        this.state.slug = t?.slug || '';
      }
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async changeStatus(id, status) {
    if (this.state.busyId) return;
    if (status === 'cancelled' && !confirm('確定取消這筆訂位？')) return;
    if (status === 'no_show' && !confirm('標記為未到？')) return;

    this.state.busyId = id;
    this.render();
    try {
      await updateReservationStatus({ reservationId: id, status });
      await this.load();
    } catch (err) {
      this.state.error = err.message_zh || err.message || '更新失敗';
    } finally {
      this.state.busyId = null;
      this.render();
    }
  }

  changeDate(offset) {
    const [y, m, d] = this.state.date.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d) + offset * 86_400_000);
    this.state.date = `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
    this.load();
  }

  render() {
    const { loading, error, date, reservations, busyId } = this.state;
    const isToday = date === todayInTaipei();

    // 統計
    const stats = reservations.reduce((acc, r) => {
      acc.total += 1;
      acc.parties += r.party_size || 0;
      if (r.status === 'seated' || r.status === 'completed') acc.shown += 1;
      return acc;
    }, { total: 0, parties: 0, shown: 0 });

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>

      <header class="hd">
        <h2>訂位管理</h2>
        <div class="date-nav">
          <button class="nav-btn" data-act="prev" aria-label="前一天">‹ 前一天</button>
          <span class="date-label ${isToday ? 'is-today' : ''}">${date}${isToday ? '（今天）' : ''}</span>
          <button class="nav-btn" data-act="next" aria-label="後一天">後一天 ›</button>
          <button class="reload" data-act="reload" aria-label="重新整理">⟳ 重整</button>
        </div>
      </header>

      ${error ? `<div class="alert">${escapeHtml(error)}</div>` : ''}

      <div class="stats">
        今天 <strong>${stats.total}</strong> 組訂位 · 共 <strong>${stats.parties}</strong> 位客人 · 已到店 <strong>${stats.shown}</strong> 組
      </div>

      ${loading ? `
        <div class="msg">載入中…</div>
      ` : reservations.length === 0 ? `
        <div class="msg">
          這天還沒有訂位。<br/>
          <span class="msg__hint">客人打電話來訂位？幫他登記一筆：</span><br/>
          ${this.state.slug ? `<a class="add-btn" href="#/reserve/${escapeHtml(this.state.slug)}">＋ 幫客人登記訂位</a>` : ''}
        </div>
      ` : `
        <ul class="list">
          ${reservations.map((r) => this.row(r, busyId === r.id)).join('')}
        </ul>
      `}
    `;

    this.bind();
  }

  row(r, busy) {
    const time = formatTime(r.reserved_at);
    const color = STATUS_COLORS[r.status] || '#666';
    const label = STATUS_LABELS[r.status] || r.status;
    const isActive = r.status === 'pending' || r.status === 'confirmed';

    return `
      <li class="row ${busy ? 'is-busy' : ''}">
        <div class="row__time">
          <div class="row__time-text">${time}</div>
          <div class="row__party">${r.party_size} 位</div>
        </div>

        <div class="row__main">
          <div class="row__name">${escapeHtml(r.guest_name)}</div>
          <div class="row__meta">
            <span>${escapeHtml(r.guest_phone)}</span>
            ${(r.table_labels && r.table_labels.length > 0) ? `
              <span>· 桌 ${r.table_labels.map(escapeHtml).join(' + ')}${r.table_labels.length > 1 ? ` <strong>（併 ${r.table_labels.length} 桌）</strong>` : ''}</span>
            ` : r.table_label ? `<span>· ${escapeHtml(r.table_label)}</span>` : ''}
            ${r.note ? `<span class="row__note">· 備註：${escapeHtml(r.note)}</span>` : ''}
          </div>
        </div>

        <div class="row__status" style="color: ${color}; border-color: ${color};">${label}</div>

        ${isActive ? `
          <div class="row__actions">
            <button class="act act--seated" data-act="seated" data-id="${r.id}">就座</button>
            <button class="act act--cancel" data-act="cancelled" data-id="${r.id}">取消</button>
            <button class="act act--noshow" data-act="no_show" data-id="${r.id}">未到</button>
          </div>
        ` : r.status === 'seated' ? `
          <div class="row__actions">
            <button class="act act--complete" data-act="completed" data-id="${r.id}">完成</button>
          </div>
        ` : ''}
      </li>
    `;
  }

  bind() {
    const root = this.shadowRoot;
    root.querySelector('[data-act="prev"]')?.addEventListener('click', () => this.changeDate(-1));
    root.querySelector('[data-act="next"]')?.addEventListener('click', () => this.changeDate(1));
    root.querySelector('[data-act="reload"]')?.addEventListener('click', () => this.load());

    root.querySelectorAll('.act').forEach((b) => {
      b.addEventListener('click', () => this.changeStatus(b.dataset.id, b.dataset.act));
    });
  }

  styles() {
    return `
      :host { display: block; padding: 1rem; max-width: 960px; margin: 0 auto; font-family: var(--font-sans, -apple-system, BlinkMacSystemFont, 'PingFang TC', sans-serif); color: var(--fg-base, #2a2a2a); }
      .hd { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.75rem; }
      h2 { margin: 0; font-size: 1.4rem; }
      .date-nav { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
      .nav-btn, .reload { min-height: 36px; padding: 0 0.8rem; border-radius: var(--r-full, 999px); border: 1px solid var(--border, #ddd); background: var(--bg-elevated, #fff); color: var(--fg-base, #2a2a2a); font-size: 0.9rem; font-family: inherit; cursor: pointer; white-space: nowrap; }
      .nav-btn:hover, .reload:hover { border-color: var(--brand-primary, #c97b4f); color: var(--brand-primary, #c97b4f); }
      .date-label { padding: 0 0.5rem; font-size: 1rem; text-align: center; white-space: nowrap; }
      .date-label.is-today { color: var(--brand-primary, #c97b4f); font-weight: 600; }
      .alert { background: #fff3cd; color: #856404; padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem; }
      .stats { color: var(--fg-muted, #666); margin-bottom: 1rem; font-size: 0.95rem; }
      .stats strong { color: var(--fg-base, #2a2a2a); }
      .msg__hint { color: var(--fg-muted); font-size: .85rem; }
      .add-btn { display: inline-block; margin-top: var(--s-3); background: var(--brand-primary); color: var(--fg-on-brand, #fff); padding: var(--s-2) var(--s-5); border-radius: var(--r-md); text-decoration: none; font-weight: 600; }
      .msg { text-align: center; color: var(--fg-dim, #999); padding: 3rem 0; }
      .list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
      .row { display: grid; grid-template-columns: 80px minmax(0, 1fr) auto; gap: 0.75rem 1rem; align-items: center; padding: 0.85rem 1rem; background: var(--bg-elevated, #fff); border-radius: 10px; border: 1px solid var(--border, #eee); }
      .row.is-busy { opacity: 0.5; pointer-events: none; }
      .row__time { text-align: center; }
      .row__time-text { font-size: 1.15rem; font-weight: 600; }
      .row__party { color: var(--fg-muted, #888); font-size: 0.85rem; margin-top: 0.2rem; }
      .row__name { font-weight: 600; font-size: 1rem; }
      .row__meta { color: var(--fg-muted, #888); font-size: 0.85rem; margin-top: 0.25rem; display: flex; gap: 0.5rem; flex-wrap: wrap; }
      .row__note { color: var(--brand-primary, #c97b4f); }
      .row__status { padding: 0.3rem 0.7rem; border: 1px solid; border-radius: 999px; font-size: 0.85rem; font-weight: 500; white-space: nowrap; }
      .row__actions { grid-column: 1 / -1; display: flex; gap: 0.5rem; padding-top: 0.5rem; border-top: 1px dashed var(--border, #eee); flex-wrap: wrap; }
      .act { padding: 0.45rem 0.9rem; border: 1px solid var(--border, #ddd); background: var(--bg-elevated, #fff); border-radius: 8px; font-size: 0.85rem; font-family: inherit; cursor: pointer; }
      .act--seated { border-color: #4A90B8; color: #4A90B8; }
      .act--cancel { border-color: #D9534F; color: #D9534F; }
      .act--noshow { border-color: #B45252; color: #B45252; }
      .act--complete { border-color: #2a9d8f; color: #2a9d8f; }
      .act:hover { background: var(--bg-surface, #f8f5f0); }

      @media (min-width: 600px) {
        .row { grid-template-columns: 80px 1fr auto auto; }
        .row__actions { grid-column: auto; padding-top: 0; border-top: 0; }
      }
    `;
  }
}

if (!customElements.get('pos-reservations')) {
  customElements.define('pos-reservations', PosReservations);
}
